import {
  Stack,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { TenantType } from 'customers/tenants-common-view/lib/schema/Tenant.types';
import { useFeatureFlags } from './useFeatureFlags';
import { useToggleFeatureFlag } from './useToggleFeatureFlag';
import { TenantFlagToggle } from '../../TenantCardSummary/TenantFlagToggle';
import OtpModal from 'common/Modals/OtpModal';
import { OTPRequestType } from 'constants/URLEnums';
import { useMemo, useState } from 'react';

const HEADER_STYLES = { color: '#11567F', fontWeight: 600 };
const CELL_SX_BASE = { paddingY: '12px' };

const FLAG_COLUMNS = [
  {
    id: 'displayName',
    label: 'Display Name',
    getValue: (flag: any) => flag?.displayName || flag?.name || '-',
    cellSx: CELL_SX_BASE,
    headerSx: CELL_SX_BASE,
    render: (flag: any) => (
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {flag?.displayName || flag?.name || '-'}
      </Typography>
    ),
  },
  {
    id: 'name',
    label: 'Name',
    getValue: (flag: any) => flag?.name || '-',
    cellSx: CELL_SX_BASE,
    headerSx: CELL_SX_BASE,
    render: (flag: any) => (
      <Typography variant="body2" color="text.secondary">
        {flag?.name || '-'}
      </Typography>
    ),
  },
  {
    id: 'description',
    label: 'Description',
    getValue: (flag: any) => flag?.description || '-',
    cellSx: CELL_SX_BASE,
    headerSx: CELL_SX_BASE,
    render: (flag: any) => (
      <Typography variant="body2" color="text.secondary">
        {flag?.description || '-'}
      </Typography>
    ),
  },
  {
    id: 'app',
    label: 'App',
    getValue: (flag: any) => flag?.app || '-',
    cellSx: { ...CELL_SX_BASE, width: '120px' },
    headerSx: { ...CELL_SX_BASE, width: '120px' },
    render: (flag: any) => (
      <Typography variant="body2" color="text.secondary">
        {flag?.app || '-'}
      </Typography>
    ),
  },
  {
    id: 'appFamily',
    label: 'App Family',
    getValue: (flag: any) => flag?.appFamily || '-',
    cellSx: { ...CELL_SX_BASE, width: '140px' },
    headerSx: { ...CELL_SX_BASE, width: '140px' },
    render: (flag: any) => (
      <Typography variant="body2" color="text.secondary">
        {flag?.appFamily || '-'}
      </Typography>
    ),
  },
] as const;

interface FeatureFlagsProps {
  tenant: TenantType;
}

export const FeatureFlags = (props: FeatureFlagsProps) => {
  const { tenant } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const { featureFlags, isLoading, refreshFeatureFlags } = useFeatureFlags({
    tenant,
  });

  const {
    openOTP,
    setOpenOTP,
    step,
    setStep,
    onSubmit,
    setOtpType,
    setToggledFlag,
    sendOTPIsLoading,
    toggleFeatureFlagIsLoading,
    getTitleAndReason,
  } = useToggleFeatureFlag({
    tenant,
    onToggleSuccess: refreshFeatureFlags,
  });

  const handleFlagToggle = (flag: any) => {
    setToggledFlag(flag);
    setOtpType(OTPRequestType.TENANT_FLAG_TOGGLE_OTP);
    setOpenOTP(true);
  };

  const statusColumn = useMemo(
    () => ({
      id: 'status',
      label: 'Status',
      headerSx: { ...CELL_SX_BASE, textAlign: 'center' as const, width: '120px' },
      cellSx: { ...CELL_SX_BASE, textAlign: 'center' as const },
      render: (flag: any) => (
        <Tooltip title="Feature flags are not allowed to be toggled.">
          <span>
            <TenantFlagToggle
              checked={flag?.status === 'ENABLED'}
              onChange={() => handleFlagToggle(flag)}
              disabled
            />
          </span>
        </Tooltip>
      ),
    }),
    [],
  );

  const columns = useMemo(
    () => [...FLAG_COLUMNS, statusColumn],
    [statusColumn],
  );

  const filteredFlags = useMemo(() => {
    if (!searchQuery.trim()) {
      return featureFlags;
    }
    const query = searchQuery.toLowerCase();
    return featureFlags.filter(
      (flag: any) =>
        (flag?.displayName || flag?.name || '')
          .toLowerCase()
          .includes(query) ||
        (flag?.description || '').toLowerCase().includes(query) ||
        (flag?.app || '').toLowerCase().includes(query) ||
        (flag?.appFamily || '').toLowerCase().includes(query),
    );
  }, [featureFlags, searchQuery]);

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          placeholder="Search feature flags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ maxWidth: 400, mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        {isLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Loading feature flags...
            </Typography>
          </Box>
        ) : filteredFlags.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery
                ? 'No feature flags found matching your search.'
                : 'No feature flags available for this tenant.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ background: '#F4F9FA' }}>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} sx={col.headerSx}>
                      <Typography sx={HEADER_STYLES}>{col.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFlags.map((flag: any, index: number) => (
                  <TableRow key={flag?.featureId || flag?.name || index} hover>
                    {columns.map((col) => (
                      <TableCell key={col.id} sx={col.cellSx}>
                        {col.render(flag)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      <OtpModal
        open={openOTP}
        setOpen={setOpenOTP}
        title={getTitleAndReason()?.title}
        subTitle={getTitleAndReason()?.subTitle}
        onSubmit={(val) => {
          onSubmit(val);
        }}
        isSubmitting={
          step === 1 ? sendOTPIsLoading : toggleFeatureFlagIsLoading
        }
        otpUser="Ops admin"
        step={step}
        setStep={setStep}
        reasonHeading={getTitleAndReason()?.reasonHeading}
        otpType={getTitleAndReason()?.otpType}
      />
    </Stack>
  );
};
